// SEQ-182: one formatting function for every typed P&L metric unit.
//
// Run: deno test --allow-read src/lib/pnl/formatMetricValue.test.ts
// Run: npm run verify:pnl-format
//
// This module intentionally has exactly one job: turn a (value, unit,
// decimals) triple into display text. It reuses `formatCurrency` from
// src/lib/calculations.ts for both currency units (rather than
// reimplementing AUD formatting) so Safe Mode masking applies to currency
// and currency_per_hour ONLY, exactly as the ticket's P&L Table Display
// section requires ("Do not prepend $ to non-financial values") and as the
// existing `formatPercentage` already does not mask
// (src/lib/calculations.ts:120-122, against `formatCurrency` at :74-76).
//
// NULL PLACEHOLDER DECISION: a null value renders as "N/A" by default,
// matching the existing convention in
// src/features/pnlAnalytics/pnlAnalyticsFormat.ts's `formatPnlMetric`
// (`if (value === null) return "N/A";`), and containing no en dash or em
// dash. A caller that needs something else (e.g. an empty string for a
// table cell) can pass `opts.nullPlaceholder`.
//
// DECIMALS DEFAULT DECISION: when the caller does not pass `decimals`, each
// unit falls back to the same default its registry entry uses in
// pnlAnalytics.ts (plan section 4.2/4.3): currency and percent default to 2,
// hours defaults to 1 (matching the ticket's "428.5 hrs" example exactly),
// count defaults to 0, ratio defaults to 1, currency_per_hour defaults to 2.

import { formatCurrency } from "../calculations";
import type { PnlUnit } from "./pnlAnalytics";

export type { PnlUnit };

export interface FormatMetricValueOptions {
  /** Placeholder rendered for a null value. Defaults to "N/A". */
  nullPlaceholder?: string;
}

const DEFAULT_DECIMALS: Record<PnlUnit, number> = {
  currency: 2,
  currency_per_hour: 2,
  percent: 2,
  hours: 1,
  count: 0,
  ratio: 1,
};

/**
 * True when `value` would display as zero at its unit's default precision
 * (for example 0.004 dollars, or -0.02 hours). Decided from the number, not
 * the text, so a Safe Mode mask cannot change the answer.
 */
export function roundsToZeroAtDisplayPrecision(value: number, unit: PnlUnit): boolean {
  return Math.round(Math.abs(value) * 10 ** DEFAULT_DECIMALS[unit]) === 0;
}

function formatPlainNumber(value: number, decimals: number): string {
  return new Intl.NumberFormat("en-AU", {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(value);
}

/**
 * Formats one metric value for display, according to its unit.
 *
 * - `currency`: AUD via `formatCurrency` (masked under Safe Mode).
 * - `currency_per_hour`: AUD via `formatCurrency` plus a "/hr" suffix
 *   (masked under Safe Mode, exactly like `currency`).
 * - `percent`: a plain number plus a "%" suffix. Not masked.
 * - `hours`: a plain number plus a " hrs" suffix. Not masked.
 * - `count`: a plain integer by default. Not masked.
 * - `ratio`: a plain number. Not masked.
 *
 * `value === null` always renders `opts.nullPlaceholder` (default "N/A"),
 * regardless of unit, before any unit-specific formatting runs.
 */
export function formatMetricValue(
  value: number | null,
  unit: PnlUnit,
  decimals?: number,
  opts?: FormatMetricValueOptions,
): string {
  if (value === null) return opts?.nullPlaceholder ?? "N/A";

  const d = decimals ?? DEFAULT_DECIMALS[unit];

  // Round 2 (code review, LOW): currency and currency_per_hour pass `d > 0`
  // into `formatCurrency`, whose own signature is `includeDecimals: boolean`
  // (0 or 2 fraction digits, never anything else -- src/lib/calculations.ts).
  // Any `decimals` value other than 0 collapses to formatCurrency's fixed
  // 2-decimal mode, so e.g. `decimals: 3` renders the same as `decimals: 2`.
  // This is an inherited limitation of formatCurrency's boolean API, not a
  // bug here; no registry entry in pnlAnalytics.ts asks for a currency
  // decimals value other than 0 or 2 today.
  switch (unit) {
    case "currency":
      return formatCurrency(value, d > 0);
    case "currency_per_hour":
      return `${formatCurrency(value, d > 0)}/hr`;
    case "percent":
      return `${formatPlainNumber(value, d)}%`;
    case "hours":
      return `${formatPlainNumber(value, d)} hrs`;
    case "count":
      return formatPlainNumber(value, d);
    case "ratio":
      return formatPlainNumber(value, d);
    default: {
      const exhaustive: never = unit;
      throw new Error(`formatMetricValue: unknown unit "${String(exhaustive)}"`);
    }
  }
}
